import { CommonModule, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';

import { NgIcon } from '@ng-icons/core';

import { SelectableWidget } from '@components/selectable-widget/selectable-widget.component';
import { CurrencyAmountComponent } from '@cxc/ui-kit/components/currency-amount';

import { CURRENCY } from '@cxc/core/enums';

import { OverviewResumeState } from '@services/state/overview-resume-state/overview-resume.state';

import {
  EXCEEDS_CEILING_NOTE,
  TOTAL_NOT_REPORTED,
  exceedsCeiling,
  hasCommitment,
  isUsableCeiling,
  lenderAmountTitle,
  portfolioShare,
} from '@utils/lender-share';

@Component({
  selector: 'core-metrics',
  standalone: true,
  imports: [
    CommonModule,
    UpperCasePipe,
    NgIcon,
    SelectableWidget,
    CurrencyAmountComponent,
  ],
  templateUrl: './core-metrics.component.html',
  styleUrl: './core-metrics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoreMetrics {
  public indicators = inject(OverviewResumeState).coreMetrics;

  exportMode = input<boolean>(false);
  selectedWidgets = input<HTMLElement[]>([]);
  // Overview's stored figures are MXN (LEN-1808) unless the user picked a
  // different display currency via the selector.
  currency = input<CURRENCY>(CURRENCY.MXN);
  widgetToggle = output<{ checked: boolean; el: HTMLElement }>();

  /**
   * The portfolio's participation, read with the same rule as a single
   * facility's (`lender-share.ts`), so the Overview and a facility never
   * disagree on what a share is (LEN-2363).
   */
  private participation = computed(() => ({
    committed_amount: this.indicators()?.committed_amount,
  }));

  /** One entry per trust: the server already merged a trust's tranches. */
  facilities = computed(() => this.indicators()?.operations?.length);

  /** The lender's commitment heads the card when there is one. */
  showsCommitment = computed(() => hasCommitment(this.participation()));

  /**
   * MAXIMUM CREDIT AMOUNT, or YOUR COMMITMENT when there is no ceiling: the
   * amount shown is then the commitment, and the facility's card already
   * says so with this same rule (LEN-2692).
   */
  amountTitle = computed(() =>
    lenderAmountTitle(
      this.participation(),
      this.indicators()?.total_credit_amount
    )
  );

  /** "of $X (Y%)" needs a ceiling to compare against. */
  comparesToCeiling = computed(
    () =>
      this.showsCommitment() &&
      isUsableCeiling(this.indicators()?.total_credit_amount)
  );

  /**
   * Trusts that count in the ceiling but reported no commitment, so the card
   * says how many are missing (LEN-2692).
   *
   * The reason changed with LEN-3334 and the notice did not: it used to be
   * that "of $X (Y%)" compared part of the commitment against the whole
   * ceiling. The percentage no longer divides those two amounts, but the
   * **`$X of $Y` line still does** -- the commitment is only of the trusts
   * that reported one, the ceiling is of all of them -- so naming the gap is
   * as necessary as before.
   */
  trustsWithoutCommitment = computed(() =>
    this.comparesToCeiling()
      ? (this.indicators()?.trusts_without_committed_amount ?? 0)
      : 0
  );

  /**
   * A commitment without a ceiling. It printed a bare "of" with nothing after
   * it; it names the missing figure instead, as the facility does (LEN-2692).
   */
  ceilingNotReported = computed(
    () => this.showsCommitment() && !this.comparesToCeiling()
  );

  /**
   * The share under the commitment, read facility by facility rather than
   * from the portfolio's two totals (LEN-3334).
   *
   * It used to divide the whole commitment by the ceiling of **every**
   * facility, so a facility nobody captured a commitment for still pushed its
   * ceiling into the divisor and the figure read low. The Portfolio tab's
   * YOUR PARTICIPATION card asks the same question and answered it the other
   * way: the same portfolio showed two percentages in two tabs. The CA1 rule
   * won, and `portfolioSharePct` is now the only place it lives.
   *
   * **The percentage no longer divides the two amounts printed above it**, so
   * the template stopped gluing it to "of $X" -- see the comment there.
   *
   * It is no longer suppressed when the portfolio's commitment exceeds the
   * portfolio's ceiling: that contradiction still shows in amber, but the
   * share now comes from the facilities that do add up, and hiding it here
   * while the Portfolio card shows it would be the same disagreement again.
   * A share above 100% is impossible by construction -- case 05 is excluded
   * facility by facility, inside the util.
   */
  private share = computed(() => portfolioShare(this.indicators()?.operations));

  sharePct = computed(() => this.share().pct);

  /**
   * Facilities the share could not measure -- no commitment, no usable
   * ceiling, or case 05. With any of them the percentage speaks for part of
   * the portfolio, so the card says how many it counted instead of letting
   * "of the facilities you are in" claim all of them (MR !1366 S3).
   */
  shareExcluded = computed(() => this.share().excluded);
  shareCounted = computed(() => this.share().counted);

  readonly exceedsCeilingNote = EXCEEDS_CEILING_NOTE;
  /** Case 02 of `docs/facility-card-states.md`, the facility's same text. */
  readonly totalNotReported = TOTAL_NOT_REPORTED;

  /** Case 05: the commitment is above the ceiling, a contradiction. */
  exceedsCeiling = computed(() =>
    exceedsCeiling(this.participation(), this.indicators()?.total_credit_amount)
  );

  /** The API sends `committed_amount: null` when no trust reported one. */
  commitmentNotReported = computed(
    () => this.indicators()?.committed_amount === null
  );

  isWidgetSelected(el: HTMLElement): boolean {
    return this.selectedWidgets().includes(el);
  }
}
